import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CaseDismissedAdmin.css';
import { CSVLink } from 'react-csv';
import moment from 'moment-timezone';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function CaseDismissedAdmin() {
    const [responses, setResponses] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [chartData, setChartData] = useState({
        monthlyData: [],
        outcomeData: [],
        reasonData: [],
        locationData: [],
        sourceData: []
    });

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

    useEffect(() => {
        fetch(`${process.env.REACT_APP_AI_FORM_SERVER}/api/getCaseDismissedResponses?_=${new Date().getTime()}`)
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setResponses(data.data);
                    processCsvData(data.data); // Process the data into CSV format
                    processChartData(data.data); // Process data for charts
                } else {
                    alert('Failed to fetch responses');
                }
            })
            .catch(error => console.log('Error fetching responses:', error));
    }, []);


    const processCsvData = (data) => {
        if (!data || data.length === 0) return; // Return early if there's no data

        // Create a header row with field names, including keys from the 'response' object
        const headers = Object.keys(data[0]).flatMap((key) => {
            if (key === 'response' && data[0][key] && typeof data[0][key] === 'object') {
                // Extract keys from the response object (key-value pairs)
                return Object.keys(data[0][key]).map(responseKey => `response_${responseKey}`);
            }
            return key; // For other fields, just return the key as is
        });

        // Format each response into a row with flattened response keys and values
        const formattedData = data.map((entry) => {
            return Object.keys(entry).flatMap((key) => {
                if (key === 'response' && entry[key] && typeof entry[key] === 'object') {
                    // Flatten the response object into individual columns for each key-value pair
                    return Object.values(entry[key]);
                }
                return entry[key]; // Return other fields as they are
            });
        });

        // Set headers and data for CSV export
        setCsvData([headers, ...formattedData]);
    };

    const processChartData = (data) => {
        if (!data || data.length === 0) return;

        // Process monthly data with outcome breakdown
        const monthlyMap = {};
        const allOutcomes = new Set();
        
        data.forEach(entry => {
            if (entry.response && entry.response.Date && entry.response["Case Decline and Reject Refer to where / outcome"]) {
                const month = moment(entry.response.Date).format('YYYY-MM');
                const outcome = entry.response["Case Decline and Reject Refer to where / outcome"];
                
                allOutcomes.add(outcome);
                
                if (!monthlyMap[month]) {
                    monthlyMap[month] = {};
                }
                
                if (!monthlyMap[month][outcome]) {
                    monthlyMap[month][outcome] = 0;
                }
                
                monthlyMap[month][outcome]++;
            }
        });

        // Convert to array format for stacked bar chart
        const monthlyData = Object.entries(monthlyMap)
            .map(([month, outcomes]) => {
                const result = { month };
                allOutcomes.forEach(outcome => {
                    result[outcome] = outcomes[outcome] || 0;
                });
                return result;
            })
            .sort((a, b) => a.month.localeCompare(b.month));

        // Process outcome data (Case Decline and Reject Refer to where / outcome) - overall
        const outcomeMap = {};
        data.forEach(entry => {
            if (entry.response && entry.response["Case Decline and Reject Refer to where / outcome"]) {
                const outcome = entry.response["Case Decline and Reject Refer to where / outcome"];
                outcomeMap[outcome] = (outcomeMap[outcome] || 0) + 1;
            }
        });
        const outcomeData = Object.entries(outcomeMap).map(([name, value]) => ({ name, value }));

        // Process reason data (Reason of Decline / Reject)
        const reasonMap = {};
        data.forEach(entry => {
            if (entry.response && entry.response["Reason of Decline / Reject"]) {
                const reason = entry.response["Reason of Decline / Reject"];
                reasonMap[reason] = (reasonMap[reason] || 0) + 1;
            }
        });
        const reasonData = Object.entries(reasonMap).map(([name, value]) => ({ name, value }));

        // Process location data
        const locationMap = {};
        data.forEach(entry => {
            if (entry.response && entry.response["Location"]) {
                const location = entry.response["Location"];
                locationMap[location] = (locationMap[location] || 0) + 1;
            }
        });
        const locationData = Object.entries(locationMap).map(([name, value]) => ({ name, value }));

        // Process source data (Source of patient / Referral from)
        const sourceMap = {};
        data.forEach(entry => {
            if (entry.response && entry.response["Source of patient / Referral from"]) {
                const source = entry.response["Source of patient / Referral from"];
                sourceMap[source] = (sourceMap[source] || 0) + 1;
            }
        });
        const sourceData = Object.entries(sourceMap).map(([name, value]) => ({ name, value }));

        setChartData({
            monthlyData,
            outcomeData,
            reasonData,
            locationData,
            sourceData
        });
    };


    // Handle CSV download with password prompt
    const handleDownload = () => {
        const password = prompt("Enter password to download CSV:");
        if (password === "Island@308") {
            setIsAuthorized(true);
        } else {
            alert("Incorrect password!");
        }
    };

    return (
        <div className="admin-container">
            <h1>Case Dismissed Form Admin Page</h1>
            {!isAuthorized ? (
                <button onClick={handleDownload} className="download-btn">
                    Key In Passcode
                </button>
            ) : (
                <div style={{ width: '100%', padding: '20px' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <CSVLink 
                            data={csvData} 
                            filename={`CaseDismissedFormResponse_${moment().tz('Asia/Kuala_Lumpur').format('DD_MM_YYYY_HH_mm_ss')}.csv`}
                            id="csvDownloadLink"
                        >
                            <button className="download-btn">Download CSV</button>
                        </CSVLink>
                    </div>

                    {/* Monthly Cases with Outcome - Full Width */}
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                        <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>Cases by Month with Outcome</h3>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={chartData.monthlyData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                {chartData.outcomeData.map((outcome, index) => (
                                    <Bar 
                                        key={outcome.name} 
                                        dataKey={outcome.name} 
                                        stackId="a" 
                                        fill={COLORS[index % COLORS.length]} 
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '20px' }}>

                        {/* Outcome Pie Chart */}
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>Case Outcome Distribution</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={chartData.outcomeData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {chartData.outcomeData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Reason Bar Chart */}
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>Reason for Decline/Reject</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={chartData.reasonData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#00C49F" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Location Bar Chart */}
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>Location Distribution</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={chartData.locationData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#FFBB28" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Source Bar Chart - Full Width */}
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>Source of Patient / Referral</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={chartData.sourceData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="value" fill="#FF8042" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CaseDismissedAdmin;

